<template>
    <b-card header-text-variant="dark">
        <div slot="header">
            <context-header :title="$t('groupSettlement.groupSettlementReview')" alternativeRoute="group-settlement/list">
            </context-header>
        </div>
        <datatable
            :url="groupSettlementsUrl"
            name="group-settlements-table"
            :columns="tColumns"
            :options="tOptions">
            <template slot="amount" slot-scope="props">
                <span>{{formatAmount(props.row.amount)}}</span>
            </template>
            <template slot="creationDate" slot-scope="props">
                <span>{{formatDate(props.row.creationDate)}}</span>
            </template>
            <template slot="dueDate" slot-scope="props">
                <span v-if="props.row.dueDate">{{formatDate(props.row.dueDate)}}</span>
                <span v-else>-</span>
            </template>
            <template slot="settledAmount" slot-scope="props">
                <span>{{formatAmount(props.row.settledAmount)}}</span>
            </template>
            <template slot="settlementDate" slot-scope="props">
                <span v-if="props.row.settlementDate">{{formatDate(props.row.settlementDate)}}</span>
                <span v-else>-</span>
            </template>            
            <template slot="debtorName" slot-scope="props" v-if="props.row.debtorId !== 0">
              <router-link :to="{name: 'approvedUserPage', params: {registrationId: props.row.debtorId}}">
                <span>{{props.row.debtorName}}</span>
              </router-link>
            </template>
            <template slot="groupSettlementStatus" slot-scope="props">
                <button class="btn btn-sm" v-bind:class="[
                  { 'btn-danger' : [groupSettlementStatus.CANCELLED, groupSettlementStatus.INVALID].includes(props.row.groupSettlementStatus) },
                  { 'btn-success': groupSettlementStatus.SETTLED === props.row.groupSettlementStatus },
                  { 'btn-info'   : ![groupSettlementStatus.CANCELLED, groupSettlementStatus.INVALID, groupSettlementStatus.SETTLED].includes(props.row.groupSettlementStatus)}
                ]">{{$t('groupSettlement.groupSettlementStatus.' + props.row.groupSettlementStatus)}}</button>
            </template>
        </datatable>
        <div class="text-right">
            <router-link :to="{name: 'registerGroupSettlementRoute'}">
                <b-button variant="info">
                {{$t('groupSettlement.createNew')}}
                <loader :show="load" type="button"></loader>
                </b-button>
            </router-link>
        </div>
    </b-card>
</template>

<script>
  import Vue from 'vue'
  import ContextHeader from '@/components/misc/context-header/ContextHeader'
  import Datatable from '@/components/misc/datatable/Datatable'
  import i18n from '@/i18n'
  import Loader from '@/components/misc/Loader'
  import { hasInvoiceReviewAllClaim } from '@/router/auth'

  export default {
    name: 'listGroupSettlement',
    components: {
      Loader,
      ContextHeader,
      Datatable
    },
    data () {
      return {
        load: false,
        groupSettlementStatus: {
          PENDING: 1,
          ACTIVE: 2,
          SETTLED: 3,
          CANCELLED: 4,
          INVALID: 5
        },
        tOptions: {
          sortable: true,
          filter: true,
          checkboxes: false,
          payloadsTotalPropertyName: 'additionalInformation', // Name of the the payload property that returns total num of rows (total, count,..)
          per_page_options: [5, 10, 15, 25, 50], // overrriden in created method
          per_page_default: 5, // overrriden in created method
          invalidRowMarker: {
            criteria: function (row, statuses) {
              return [statuses.INVALID, statuses.CANCELLED].includes(row['groupSettlementStatus'])
            }
          },
          highlightedRowMarker: {
            criteria: function (row, statuses) {
              return row['groupSettlementStatus'] === statuses.INVALID
            }
          },
          altHighlightedRowMarker: {
            criteria: function (row, statuses) {
              return row['groupSettlementStatus'] === statuses.ACTIVE
            }
          },
          detailsLink: {
            createLink: function (row) {
              return '/group-settlement/' + row['groupSettlementId']
            }
          }
        }
      }
    },
    watch: {
    },
    computed: {
      tColumns () {
        let retVal = [
          {
            columnName: 'formattedGroupSettlementNumber',
            title: i18n.t('groupSettlement.columnGroupSettlementNumber'),
            filterType: 'input',
            align: 'center',
            cell (row) {
              return row.groupSettlementNumber
            }
          },
          {
            columnName: 'creditorName',
            title: i18n.t('groupSettlement.columnCreditor'),
            filterType: 'input',
            align: 'center',
            cell (row) {
              return row.creditorName
            }
          },
          {
            columnName: 'amount',
            columnRange: [
              {
                text: i18n.t('groupSettlement.columnAmountFrom'),
                value: 'amount-from'
              },
              {
                text: i18n.t('groupSettlement.columnAmountTo'),
                value: 'amount-to'
              }
            ],
            title: i18n.t('groupSettlement.columnAmount'),
            filterType: 'rangeMoney',
            hideFilter: true,
            showSummary: false,
            align: 'left',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'creationDate',
            columnRange: [
              {
                text: i18n.t('groupSettlement.columnCreationDateRangeFrom'),
                value: 'creationDate-from'
              },
              {
                text: i18n.t('groupSettlement.columnCreationDateRangeTo'),
                value: 'creationDate-to'
              }
            ],
            title: i18n.t('groupSettlement.columnCreationDate'),
            filterType: 'date-range',
            hideFilter: true,
            align: 'center',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'dueDate',
            columnRange: [
              {
                text: i18n.t('groupSettlement.columnDueDateRangeFrom'),
                value: 'dueDate-from'
              },
              {
                text: i18n.t('groupSettlement.columnDueDateRangeTo'),
                value: 'dueDate-to'
              }
            ],
            title: i18n.t('groupSettlement.columnDueDate'),
            filterType: 'date-range',
            hideFilter: true,
            align: 'center',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'settlementDate',
            columnRange: [
              {
                text: i18n.t('groupSettlement.columnSettlementDateRangeFrom'),
                value: 'settlementDate-from'
              },
              {
                text: i18n.t('groupSettlement.columnSettlementDateRangeTo'),
                value: 'settlementDate-to'
              }
            ],
            title: i18n.t('groupSettlement.columnSettlementDate'),
            filterType: 'date-range',
            hideFilter: true,
            align: 'center',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'groupSettlementStatus',
            title: i18n.t('groupSettlement.columnStatus'),
            filterType: 'select',
            filterSelectOptions: [
              { key: i18n.t('groupSettlement.groupSettlementStatus.0'), value: '' },
              { key: i18n.t('groupSettlement.groupSettlementStatus.1'), value: 1 },
              { key: i18n.t('groupSettlement.groupSettlementStatus.2'), value: 2 },
              { key: i18n.t('groupSettlement.groupSettlementStatus.3'), value: 3 },
              { key: i18n.t('groupSettlement.groupSettlementStatus.4'), value: 4 },
              { key: i18n.t('groupSettlement.groupSettlementStatus.5'), value: 5 }
            ],
            cell (row) {
              return null
            }
          }
        ]
        if (hasInvoiceReviewAllClaim()) {
          retVal.push({
            columnName: 'debtorName',
            title: i18n.t('groupSettlement.columnDebtor'),
            filterType: 'input',
            align: 'center',
            cell (row) {
              return null
            }
          })
        }
        return retVal
      }
    },
    created () {
      this.groupSettlementsUrl = Vue.$apiConfig.groupSettlementsGet
      this.tOptions.statuses = this.groupSettlementStatus
      if (!!Vue.$config.group_settlements && !!Vue.$config.group_settlements.per_page_options) {
        this.tOptions.per_page_options = Vue.$config.group_settlements.per_page_options
      }
      if (!!Vue.$config.group_settlements && !!Vue.$config.group_settlements.per_page_defaultOption) {
        this.tOptions.per_page_default = Vue.$config.group_settlements.per_page_default
      }
    },
    methods: {}
  }

</script>

<style lang="scss" type="text/scss" scoped>

  @import '~@/assets/scss/variables.scss';

  .bg-primary {
    background-color: $primary-color-accent !important;
  }

  .text-dark {
    font-weight: 600;
    font-size: 12pt;
    color: $font-color-dark !important;
  }

  @media (min-width: 1400px) {
      .container {
          width: 1360px;
          max-width: 100%;
      }
  }

  @media (min-width: 1680px) {
      .container {
          width: 1640px;
          max-width: 100%;
      }
  }

  @media (min-width: 1920px) {
      .container {
          width: 1860px;
          max-width: 100%;
      }
  }
</style>



// WEBPACK FOOTER //
// ListGroupSettlement.vue?ad8615f4